import React from "react";
import "../../styles/atoms.scss";

const titleStyle = {
  textAlign: "left",
  width: "100%",
  fontSize: "24px",
};

const Policy = () => {
  return (
    <div className="static-hero">
      <h1 className="big-title">Privacy Policy</h1>

      <div className="static-content">
        <p>
          This Privacy Policy describes how UUD (“UX/UI Designers,” “we,” or “us”) collects, uses,
          and shares information when you use our website and related services (the “Service”). By
          using the Service, you agree to this policy. If you do not agree, please do not use the
          Service.
        </p>
        <p>
          <strong>Effective date:</strong> March 29, 2026. We may update this policy from time to
          time; the revised version will be indicated by an updated date, and material changes may
          be communicated through the Service or by email where appropriate.
        </p>

        <h2 className="title" style={titleStyle}>
          1. What UUD is
        </h2>
        <p>
          UUD is an online catalog and community-oriented space for UX and UI designers. We surface
          curated tools, resources, ideas, and apps, and offer optional accounts so you can save
          favorites and build a public or private designer profile. Some features (such as the color
          explorer) may store preferences only in your browser.
        </p>

        <h2 className="title" style={titleStyle}>
          2. Information we collect
        </h2>
        <p>
          <strong>Account and profile (if you register).</strong> When you create an account, we
          collect the data you provide and that our authentication system needs, including email
          address, username, password (stored securely by our backend), and profile fields such as
          display name, nationality, role, short bio, optional links (for example portfolio or
          social URLs), optional profile image, and preferences like profile visibility and email
          options. We use this information to operate your account, show your profile to others when
          you choose to make it public, and communicate with you about the Service (for example
          verification, security, or password reset messages sent by our platform).
        </p>
        <p>
          <strong>Favorites.</strong> If you are signed in, we store which catalog items you save as
          favorites, linked to your account, so we can show them to you (and on your public
          profile when applicable).
        </p>
        <p>
          <strong>Usage and analytics.</strong> Unless analytics are disabled in our deployment,
          we collect limited usage data to understand how the Service is used and to improve it.
          This can include a random visitor identifier stored in your browser (for example in{" "}
          <code>localStorage</code>), the page or path you view, approximate location derived from
          your network (country and city when available), browser user-agent string, and events such
          as catalog item interactions or certain UI actions. This data is sent to our own backend
          for aggregation; it is not used for third-party advertising on our behalf.
        </p>
        <p>
          <strong>Technical data.</strong> Like most websites, our servers and hosting receive
          technical information necessary to deliver the Service (for example IP address and
          request metadata in server logs, depending on configuration).
        </p>
        <p>
          <strong>Signup or access requests.</strong> If we operate an invitation or signup flow, we
          may process email addresses and related request metadata to manage access.
        </p>

        <h2 className="title" style={titleStyle}>
          3. How we use information
        </h2>
        <p>We use the information above to:</p>
        <p>
          provide, secure, and improve the Service;
          <br />
          authenticate you and maintain your session;
          <br />
          display profiles and favorites according to your settings;
          <br />
          analyze aggregated usage and fix issues;
          <br />
          comply with law and respond to valid legal requests;
          <br />
          protect the rights, safety, and integrity of UUD and our users.
        </p>
        <p>
          We do <strong>not</strong> use the Service to collect credit card numbers, government IDs,
          or physical mailing addresses for e-commerce or shipping, because we do not sell physical
          goods or process payments through this website in that manner.
        </p>

        <h2 className="title" style={titleStyle}>
          4. Third parties and subprocessors
        </h2>
        <p>
          We host data and run core functionality on infrastructure that uses{" "}
          <strong>PocketBase</strong> (or compatible) backends and related file storage. Those
          providers process data on our behalf under our configuration.
        </p>
        <p>
          To resolve approximate location for analytics, your browser may contact public geo
          lookup services (for example <strong>ipwho.is</strong> or <strong>ipapi.co</strong>). Those
          requests go from your device to those services; their privacy policies govern their
          handling of any data they receive.
        </p>
        <p>
          Catalog entries link to external tools and sites. We do not control those third parties;
          you should read their privacy policies before using them.
        </p>

        <h2 className="title" style={titleStyle}>
          5. Cookies, local storage, and similar technologies
        </h2>
        <p>
          We use cookies or similar technologies as needed for authentication, session management,
          and preferences. Analytics may use a persistent identifier in browser storage to correlate
          events without requiring a login. You can clear site data or use browser controls to limit
          storage; some features may not work correctly if you block essential cookies or storage.
        </p>

        <h2 className="title" style={titleStyle}>
          6. Retention
        </h2>
        <p>
          We retain account and profile information while your account exists and for a reasonable
          period afterward for backups, security, and legal compliance. Analytics events are
          retained for as long as needed for statistics and improvement, unless a shorter period is
          configured on our systems. You may request deletion of your account data as described
          below.
        </p>

        <h2 className="title" style={titleStyle}>
          7. Your choices and rights
        </h2>
        <p>
          Depending on where you live, you may have rights to access, correct, delete, or export
          personal data, or to object to or restrict certain processing. You can update much of
          your profile inside the Service when logged in. For other requests (including account
          deletion), contact us using the email below. We may need to verify your identity before
          acting on a request.
        </p>
        <p>
          Where analytics are optional for our deployment, the site may be built with an
          environment flag that disables client-side analytics entirely.
        </p>

        <h2 className="title" style={titleStyle}>
          8. Children
        </h2>
        <p>
          The Service is not directed at children under 13, and we do not knowingly collect
          personal information from children under 13. If you believe we have collected such
          information, contact us and we will take steps to delete it.
        </p>

        <h2 className="title" style={titleStyle}>
          9. Security
        </h2>
        <p>
          We use administrative, technical, and organizational measures appropriate to the nature of
          the Service to protect personal information. No method of transmission or storage is
          completely secure; we cannot guarantee absolute security.
        </p>

        <h2 className="title" style={titleStyle}>
          10. International users
        </h2>
        <p>
          Our servers and subprocessors may be located in countries other than your own. By using
          the Service, you understand that your information may be transferred to and processed in
          those countries, which may have different data protection rules than your jurisdiction.
        </p>

        <h2 className="title" style={titleStyle}>
          11. Contact
        </h2>
        <p>
          For questions, concerns, or requests regarding this Privacy Policy or your personal
          information, contact us at:{" "}
          <a href="mailto:info@uud.io">info@uud.io</a>
        </p>

        <p>
          By using UUD, you acknowledge that you have read this Privacy Policy and agree to its
          terms. Thank you for trusting UUD with your privacy.
        </p>
      </div>
    </div>
  );
};

export default Policy;
